import type { CSSObject } from "@emotion/react";
import { Theme } from "@mui/material";
import type { Palette } from "@mui/material/styles";
import { darken, getContrastRatio, lighten } from "@mui/material/styles";
import { sharedNeutrals } from "@themes/tokens/shared";

import { resolvePaletteVar } from "./gradients";
import { paletteShade } from "./paletteShade";

export enum ButtonState {
  DEFAULT = "default",
  HOVER = "hover",
  ACTIVE = "active"
}

export enum ButtonTreatment {
  STANDARD = "standard",
  PRIMARY_CONTAINED = "primaryContained",
  PRIMARY_CONTAINED_DEEP = "primaryContainedDeep"
}

export const getButtonDisabledStyles = (theme: Theme) => ({
  "&::after": {
    display: "none"
  },
  "&::before": {
    display: "none"
  },
  background: theme.vars.palette.action.disabledBackground,
  borderColor: "transparent",
  boxShadow: "none",
  color: theme.vars.palette.grey["400"],
  cursor: "not-allowed"
});

type ButtonStyles = CSSObject;

/**
 * A value is unsafe for JS color utilities (`alpha`/`darken`/`lighten`) when it
 * is a CSS variable string such as `var(--mui-palette-primary-main)`. In MUI v7
 * CSS-variables mode `theme.vars.*` (and helpers like `paletteShade`) return
 * exactly these strings, which the color utilities cannot parse.
 */
const isCssVar = (color?: string): color is string =>
  !!color && color.includes("var(");

// Matches the palette CSS variables MUI emits, e.g.
//   var(--mui-palette-primary-main)
//   var(--mui-palette-primary-500)
// Capturing the colour key (`primary`) and the shade/variant key (`main`/`500`).
const PALETTE_CSS_VAR_RE =
  /^var\(--mui-palette-([A-Za-z0-9]+)-([A-Za-z0-9]+)\)$/;

const readShade = (
  palette: Palette | undefined,
  colorKey: string,
  shadeKey: string
): string | undefined => {
  if (!palette) return undefined;
  // Numeric shades (e.g. `primary[500]`) are intentionally not augmented onto
  // `PaletteColor` (see `paletteShade.ts`), so dynamic access needs a cast.
  // This mirrors the existing `paletteShade` helper rather than introducing a
  // broad `any`.
  const colorGroup = (
    palette as unknown as Record<string, Record<string, string> | undefined>
  )[colorKey];
  return colorGroup?.[shadeKey];
};

/**
 * Resolves a palette CSS-variable string to its concrete per-scheme colour
 * values, so the result can be safely handed to `alpha`/`darken`/`lighten`.
 *
 * Returns `null` when the input is not a recognised palette variable or the
 * token cannot be found in both schemes — callers must then fall back to a
 * literal colour and never pass the variable to a colour utility.
 */
const resolveVarToSchemeColors = (
  theme: Theme,
  cssVar: string
): { dark: string; light: string } | null => {
  const match = cssVar.match(PALETTE_CSS_VAR_RE);
  // Not a two-segment palette var — try the general resolver, which also
  // follows a semantic `ui` token through to the palette var it names.
  if (!match) return resolvePaletteVar(theme, cssVar);

  const [, colorKey, shadeKey] = match;
  const light = readShade(
    theme.colorSchemes.light?.palette,
    colorKey,
    shadeKey
  );
  const dark = readShade(theme.colorSchemes.dark?.palette, colorKey, shadeKey);

  if (!light || !dark) return null;
  return { dark, light };
};

/**
 * The floor a white label has to clear before it gives way to a dark one:
 * WCAG's 3:1 for large text.
 */
const WHITE_LABEL_MIN_CONTRAST = 3;

/**
 * The fills that keep a white label under the floor, by resolved colour.
 * One: the light scheme's amber (#E98945, 2.59:1), which the product keeps
 * white beside its red and green neighbours rather than turning dark. A
 * knowing step under WCAG on this one colour, carved out here rather than
 * as a lower floor for all: the pale dark-scheme fills the rule exists for
 * (Ocean dark `primary[500]`, 2.87:1) still take a dark label.
 */
const WHITE_LABEL_EXCEPTIONS: ReadonlySet<string> = new Set(["#E98945"]);

/**
 * White on `background`, unless white cannot be read on it.
 *
 * Restored. It shipped in the nav facelift, and 52f02f8c7 took it out along
 * with the glass treatment it sat beside - "a filled button's label back to
 * white unconditionally". That holds while every filled button is a dark
 * brand colour, and the dark schemes put the accent at the LIGHT end of the
 * inverted ramp: the sign-in button on Ocean dark is a pale blue, and white
 * on it is unreadable. Reported from the login page.
 *
 * Not simply "whichever contrasts more": by that rule the light scheme's red
 * took a dark label at a perfectly legible 3.85:1. White stays the brand's
 * button text wherever it clears the floor.
 */
const contrastTextFor = (background: string): string =>
  WHITE_LABEL_EXCEPTIONS.has(background.toUpperCase()) ||
  getContrastRatio(background, sharedNeutrals.white) >= WHITE_LABEL_MIN_CONTRAST
    ? sharedNeutrals.white
    : sharedNeutrals.black;

const PRIMARY_CONTAINED_LABEL_MIN_CONTRAST = 4.5;

/**
 * How far the deep primary fill is raised before its label is chosen. Luna's
 * `primary.400` reads 4.26:1 against the dark label and the ramp has no step
 * between it and `primary.300`, so the shade cannot carry the label as it
 * stands. 8% is the smallest uniform lift that clears 4.5:1 on all five
 * palettes, which is what buys the dark label without a per-palette branch.
 */
const DEEP_LABEL_LIFT = 0.08;

/**
 * How far the fill is lifted for the button's edge, per scheme.
 *
 * Dark cannot go much lower: the dark schemes put the primary fill within
 * 1.02-1.10:1 of the page, so this border is the only thing that finds the
 * button's outline. 20% is the smallest step that keeps that edge at 1.63:1 or
 * better against the page on all five palettes (luna is the worst), while
 * dropping the ring itself from the light scheme's 4.9:1 against the fill to
 * 1.85:1. Light keeps its 50%: there the white page separates the button on its
 * own, and the bright edge reads as intended.
 */
const BUTTON_BORDER_LIGHTEN = { dark: 0.2, light: 0.5 } as const;

const STATE_SHIFT: Record<ButtonState, number> = {
  [ButtonState.ACTIVE]: 0.16,
  [ButtonState.DEFAULT]: 0,
  [ButtonState.HOVER]: 0.08
};

/**
 * Moves the fill toward mid-tone, away from whichever of black or white it sits
 * nearer. A fixed direction cannot work on both ends of the ramp: the dark
 * schemes park the primary fill within 1.10:1 of black, where darkening it is
 * invisible, and the pale semantic fills sit the same distance from white.
 * Measured over all five palettes, this keeps every step at 1.14:1 or more
 * against the state before it, where a fixed darken bottomed out at 1.02:1.
 */
const shiftForState = (fill: string, state: ButtonState): string => {
  const shift = STATE_SHIFT[state];
  if (!shift) return fill;
  return getContrastRatio(fill, sharedNeutrals.black) <
    getContrastRatio(fill, sharedNeutrals.white)
    ? lighten(fill, shift)
    : darken(fill, shift);
};

const buildColoredButtonStyles = (
  color: string,
  state: ButtonState,
  scheme: "dark" | "light",
  treatment: ButtonTreatment = ButtonTreatment.STANDARD
): ButtonStyles => {
  const candidate =
    treatment === ButtonTreatment.PRIMARY_CONTAINED_DEEP
      ? lighten(color, DEEP_LABEL_LIFT)
      : color;
  const takesDarkLabel =
    treatment !== ButtonTreatment.STANDARD &&
    getContrastRatio(candidate, sharedNeutrals.black) >=
      PRIMARY_CONTAINED_LABEL_MIN_CONTRAST;
  const resting = takesDarkLabel ? candidate : color;
  const fill = shiftForState(resting, state);

  return {
    background: fill,
    border: `1px solid ${lighten(fill, BUTTON_BORDER_LIGHTEN[scheme])}`,
    /**
     * Chosen from the RESTING fill and held for all three states. Re-deciding
     * it per state flips the label on nine palette/colour pairs — the pale dark
     * fills cross the 3:1 floor mid-press, and the amber carve-out is keyed on
     * an exact colour a shifted fill no longer matches — so the label flickered
     * between white and black on hover. Every state is asserted against its own
     * fill in `primaryContainedButton.test.ts`.
     */
    color: takesDarkLabel ? sharedNeutrals.black : contrastTextFor(resting)
  };
};

/**
 * Pure style builder — takes the theme as an argument rather than reading it
 * from `useTheme`. It is routinely called conditionally (only for a given
 * variant, or only when a button group renders) and from inside `styled()`
 * interpolations, so it must not be a hook: doing so changed the hook order
 * between renders of its callers.
 */
export const getButtonStyles = (
  theme: Theme,
  baseColor?: string,
  state: ButtonState = ButtonState.DEFAULT,
  active?: boolean,
  _enableBoxShadow?: boolean,
  /**
   * Corner radius for the glass button, in px. Defaults to the control radius.
   *
   * The glass button paints its surface overlay and its gradient border onto
   * `::after`/`::before`, so a caller cannot correct their radius through `sx`
   * — a smaller button has to pass it here or the ring keeps the full-size
   * corners and visibly detaches from the shrunken body.
   *
   * Coloured buttons ignore this: their radius comes from the `MuiButton`
   * theme override, which the caller can override on the root element.
   */
  borderRadius?: number,
  treatment: ButtonTreatment = ButtonTreatment.STANDARD
): ButtonStyles => {
  const resolvedBorderRadius = borderRadius ?? theme.shape.borderRadiusButton;

  if (!baseColor) {
    return {
      backgroundColor: "transparent",
      border: `${active ? 2 : 1}px solid ${theme.vars.palette.border.subtle}`,
      borderRadius: `${resolvedBorderRadius}px`,
      boxShadow: "none",
      color: theme.vars.palette.text.secondary
    };
  }

  // Colored button configuration (primary, error, warning, success, info, …).
  //
  // `baseColor` may be either a concrete colour (e.g. `theme.palette.x.main`,
  // a literal hex) or — after the MUI v7 migration — a CSS-variable string
  // (e.g. `theme.vars.palette.primary.main`, `paletteShade(theme, …)`). The
  // colour utilities cannot parse CSS variables, so we branch on the shape.

  /**
   * The light style at the root plus a dark override via `applyStyles`, as
   * recommended by MUI when CSS variables cannot be used with colour utils.
   * Emitted even for a mode-independent fill, because the BORDER differs by
   * scheme: `theme.palette.mode` is pinned to the default scheme under CSS
   * variables, so the scheme can only be told apart by which branch emits.
   */
  const schemePair = (lightColor: string, darkColor: string): ButtonStyles => ({
    ...buildColoredButtonStyles(lightColor, state, "light", treatment),
    // `applyStyles` is typed against `@mui/system`'s `CSSObject`, whose index
    // signature is structurally incompatible with `@emotion/react`'s
    // `CSSObject` we expose here. The runtime shape is identical, so this is a
    // pure type-bridge cast at the interop boundary.
    ...(theme.applyStyles(
      "dark",
      buildColoredButtonStyles(darkColor, state, "dark", treatment)
    ) as ButtonStyles)
  });

  if (isCssVar(baseColor)) {
    const resolved = resolveVarToSchemeColors(theme, baseColor);
    if (resolved) return schemePair(resolved.light, resolved.dark);

    // Unresolvable variable: fall back to the primary accent so we never feed a
    // CSS variable into `lighten`. `theme.palette` (not `theme.vars`) is the
    // resolved literal. Mirrors the fallback in `gradients.ts`.
    const fallback = theme.palette.primary.main;
    return schemePair(fallback, fallback);
  }

  return schemePair(baseColor, baseColor);
};

/** The `color` values a soft-capable button offers. */
export type ButtonColor =
  | "primary"
  | "secondary"
  | "error"
  | "warning"
  | "info"
  | "success";

export interface SoftTreatment {
  active: string;
  background: string;
  hover: string;
  ring: string;
  text: string;
}

export const getSoftPrimaryTreatment = (theme: Theme): SoftTreatment => {
  const selected = theme.vars.ui.sidebar.item.selected;
  const press = (pct: number) =>
    `color-mix(in srgb, ${selected.text} ${pct}%, ${selected.background})`;

  return {
    active: press(14),
    background: selected.background,
    hover: press(8),
    ring: selected.ring,
    text: selected.text
  };
};

export const getSoftButtonStyles = (
  theme: Theme,
  color: ButtonColor
): ButtonStyles => {
  if (color === "primary") {
    const { active, background, hover, ring, text } =
      getSoftPrimaryTreatment(theme);
    return {
      "&:active": { backgroundColor: active },
      "&:hover": { backgroundColor: hover },
      backgroundColor: background,
      border: `1px solid ${ring}`,
      color: text,
      fontWeight: theme.typography.fontWeightBold
    };
  }

  const isSecondary = color === "secondary";
  /**
   * `secondary` has no palette entry of its own, so it takes the resting grey
   * its contained sibling takes. Both steps are named because the dark
   * override below is emitted whichever scheme these styles are built in.
   */
  const secondaryTone = {
    dark: paletteShade(theme, "primary", 600),
    light: paletteShade(theme, "primary", 100)
  };
  const tone = isSecondary
    ? secondaryTone[theme.palette.mode]
    : theme.vars.palette[color].main;

  const ground = theme.vars.palette.background.default;
  const wash = (pct: number) =>
    `color-mix(in srgb, ${tone} ${pct}%, ${ground})`;
  const ring = (pct: number) =>
    `color-mix(in srgb, ${tone} ${pct}%, transparent)`;

  return {
    "&:active": { backgroundColor: wash(22) },
    "&:hover": { backgroundColor: wash(12) },
    backgroundColor: wash(isSecondary ? 15 : 4),
    border: `1px solid ${ring(isSecondary ? 85 : 45)}`,
    color: `color-mix(in srgb, ${tone} ${isSecondary ? 35 : 55}%, ${sharedNeutrals.black})`,
    fontWeight: theme.typography.fontWeightBold,

    ...(theme.applyStyles("dark", {
      "&:active": { backgroundColor: wash(26) },
      "&:hover": { backgroundColor: wash(20) },
      backgroundColor: wash(isSecondary ? 7 : 14),
      border: isSecondary
        ? `1px solid color-mix(in srgb, ${secondaryTone.dark} 30%, ${ground})`
        : `1px solid ${ring(45)}`,
      color: isSecondary ? theme.vars.palette.text.tertiary : tone
    }) as ButtonStyles)
  };
};
